import { useGlobalStore } from 'libs/lead-capture-form/state/globalStore';
import { ENTITY_TYPE } from 'libs/lead-capture-form/utils/constants';
import { EVENT_STATUS } from 'libs/lead-capture-form/utils/types';
import { memo, useEffect, useRef, useState } from 'react';

import EmbedPlaylistApp from './containers/EmbedPlaylistApp';
import { useRLEWithLead } from './helper/useRLEWithLead';

/**
 * PlayerManager doesn't do its job sometimes, so we need to retry failures
 *
 * @param {HTMLElement} playerElementRef - Reference to the HTML node in which player is loaded.
 * @param {Function} callback - function to run when player loads
 */
const getPlayerWithRetry = (playerElementRef, callback) => {
  if (!playerElementRef.current) {
    setTimeout(getPlayerWithRetry.bind(null, playerElementRef, callback), 20);
    return;
  }

  const player = window.PlayerManager.getPlayer(playerElementRef.current, false);

  if (!player) {
    setTimeout(getPlayerWithRetry.bind(null, playerElementRef, callback), 20);
    return;
  }

  callback(player);
};

const PlayerComponent = (props) => {
  const playerElementRef = useRef(null);
  const playerRef = useRef(null);
  const [loadEmbedApp, setLoadEmbedApp] = useState(false);
  const { clips, isRLE, liveEvent, playlistData, eventStatus, isEmbeddedInAppearancePage } = props;
  const { clipToPlay: clipToPlayWithLead } = useRLEWithLead();
  const { configUrl: configUrlWithLead } = clipToPlayWithLead || {};
  const entityType = useGlobalStore((store) => store.entityType);
  const hasLeadCaptureFlow = useGlobalStore((store) => store.hasNewLeadCaptureFlow);

  // Hide the playlist UI but mount the component to make polling available for single clip RLE's
  // Hide playlist UI if RLE embed setting explicitly says so
  const hidePlaylistUI = !playlistData.showPlaylist;

  useEffect(() => {
    function setPlayerElementRef(playerElementReference) {
      const playerElement = document.querySelector('.js-player');
      if (!playerElementReference.current && playerElement) {
        playerElementReference.current = playerElement;
      } else {
        setTimeout(setPlayerElementRef.bind(null, playerElementReference), 20);
      }
    }

    setPlayerElementRef(playerElementRef);
  }, []);

  useEffect(() => {
    let timeout = undefined;
    function runPlayerManager() {
      if (playerElementRef.current && window.PlayerManager) {
        /**
         * Why the logic?
         *
         * In the PHP side, when the EmbedNextController is hit, the player dom is initialized with the configUrl attached to it in a data attribute called 'data-config-url'.
         * Now when the FE is rendered, the player dom is already there and the PlayerManager.run() will load the player with the config url present in 'data-config-url'.
         * But in case of events with consolidated gate, the default config url does not contain the leadUuid, so we have to load the player with the config url present in the clipToPlay which contains the leadUuid.
         * So, we first set the config url in the player dom and then run the PlayerManager.run() to load the player with the correct config url.
         *
         * What was happening earlier?
         *
         * The player was loading first with the default config url which does not have leadUuid and then we were loading the player again with the correct config url with leadUuid, which results in two config call which results in issues at player backend side.
         */
        if (
          entityType === ENTITY_TYPE.EVENT &&
          hasLeadCaptureFlow &&
          eventStatus !== EVENT_STATUS.ENDED &&
          /** Embed without interactivity tool is also embeded in player customization page of event. There we don't want to show CG, we load configurl without leaduuid */
          !isEmbeddedInAppearancePage
        ) {
          if (configUrlWithLead) {
            playerElementRef.current.setAttribute('data-config-url', configUrlWithLead);
            window.PlayerManager.run(playerAssetUrls);
          }
        } else {
          window.PlayerManager.run(playerAssetUrls);
        }
      } else {
        timeout = setTimeout(runPlayerManager, 20);
      }
    }

    runPlayerManager();
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configUrlWithLead, entityType, hasLeadCaptureFlow]);

  useEffect(() => {
    getPlayerWithRetry(playerElementRef, (player) => {
      playerRef.current = player;
      player
        .ready()
        .then(() => {
          if (loadEmbedApp === false) {
            setLoadEmbedApp(true);
          }
        })
        .catch((err) => console.error('App init failed:', err));
    });
  }, [loadEmbedApp]);

  useEffect(() => {
    document.title = playlistData.title;
  }, [playlistData.title]);

  return (
    <>
      {loadEmbedApp && (
        <EmbedPlaylistApp
          playlistData={playlistData}
          clips={clips}
          isRLE={isRLE}
          liveEvent={liveEvent}
          player={playerRef.current}
          playerEl={playerElementRef.current}
          hidePlaylistUI={hidePlaylistUI}
        />
      )}
    </>
  );
};

export const Player = memo(PlayerComponent);
